import { useRouter } from 'next/router';
import { useLocaleContext } from 'providers/LocaleProvider';
import { useViewContext } from 'providers/ViewContextProvider';
import React, { useRef } from 'react';
import SearchIcon from '@seaweb/coral/components/icons/Search';
import Select, {
  SelectSearch,
  SelectList,
  SelectOption,
} from '@seaweb/coral/components/Select';
import { useSearchContext } from 'providers/SearchProvider';
import ElkHighlighter from 'components/shared/ElkHighlighter';
import { object, bool } from 'prop-types';
import styled from 'styled-components';
import { useNavInfoContext } from 'providers/NavInfoProvider';

const Placeholder = styled.div`
  opacity: 0.5;
  width: calc(100% - 20px);
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  padding-left: 28px;
  color: rgba(32, 35, 37, 0.3);
`;

const StyledSelectSearch = styled(SelectSearch)`
  background: transparent !important;
  border: solid 1px rgba(56, 56, 56, 0.15);
  border-radius: 4px;
  padding-left: 42px;

  @media screen and (-webkit-min-device-pixel-ratio: 0) {
    font-size: 16px;
  }

  @media screen and (min-width: 600px) and (max-width: 1024px) {
    max-width: calc(100vw - 440px);
  }

  ${({ isMobileView, isSearchFieldExpandInMobile }) => {
    if (isMobileView) {
      return isSearchFieldExpandInMobile
        ? `
          width: calc(100vw - 100px); 
          transition: 0.2s ease;`
        : `
          width: 0px; 
          border: 0; 
          background-color: transparent; 
          color: white;`;
    }
  }}
`;

const StyledSearchIcon = styled(SearchIcon)`
  position: absolute;
  opacity: 0.5;
  top: 13px;

  ${(props) =>
    !props.isWhiteHeader
      ? 'path {fill: white;}'
      : 'path {fill: #383838;opacity:0.25;}'}

  ${(props) =>
    props.isMobileView
      ? 'margin:14px 12px 12px;'
      : 'margin:12px 12px 12px 15px;'}

  @media screen and (max-width: 600px) {
    z-index: 2;
    ${(props) =>
      !props.isSearchFieldExpandInMobile
        ? 'right:28px;padding:20px;padding-left:40px;top:-11px;'
        : 'top:10px;'}
  }
`;

const StyledSelectList = styled(SelectList)`
  margin-top: 5px;
`;

function NavSearchField({ style, isWhiteHeader }) {
  const { isMobileView } = useViewContext();
  const searchInputRef = useRef(null);
  const { push } = useRouter();
  const { locale: t } = useLocaleContext();
  const { isSearchFieldExpandInMobile, setIsSearchFieldExpandInMobile } =
    useNavInfoContext();
  const { keyword, setKeyword, articles } = useSearchContext();

  const handleSearch = () => push(`/search?keyword=${keyword || ''}&page=1`);

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      if (searchInputRef.current) {
        searchInputRef.current.blur();
      }
      handleSearch();
    }
  };

  const handleIconButton = () => {
    if (!isSearchFieldExpandInMobile) {
      setIsSearchFieldExpandInMobile(true);
      if (searchInputRef.current) searchInputRef.current.focus();
    }
  };

  const overrideResetText = (state, { action, nextState }) => {
    if (action.type === 'inputBlur') {
      const overrideState = nextState;
      overrideState.value = state.value;
      return overrideState;
    }
    return nextState;
  };

  return (
    <Select
      searchValue={keyword}
      onSearch={(word) => setKeyword(word)}
      stateReducer={overrideResetText}
      onSelect={(val) => {
        if (searchInputRef.current) {
          searchInputRef.current.blur();
        }
        push(`/categories/${val[0]}/article/${val[1]}`);
      }}
    >
      <StyledSearchIcon
        isMobileView={isMobileView}
        isWhiteHeader={isWhiteHeader}
        isSearchFieldExpandInMobile={isSearchFieldExpandInMobile}
        size={isMobileView ? 20 : 16}
        onClick={handleIconButton}
      />

      <StyledSelectSearch
        isMobileView={isMobileView}
        isSearchFieldExpandInMobile={isSearchFieldExpandInMobile}
        ref={searchInputRef}
        style={{
          ...(style || {}),
          ...(!isWhiteHeader && {
            borderColor: 'white',
            color: 'white',
          }),
        }}
        onKeyPress={handleKeyPress}
        onChange={(e) => setKeyword(e.target.value.slice(0, 127))}
        rightElement={null}
      >
        {(!keyword || keyword.length === 0) &&
          (!isMobileView || isSearchFieldExpandInMobile) && (
            <Placeholder style={{ color: isWhiteHeader ? '#202325' : 'white' }}>
              {t('hc.search.question_keywords')}
            </Placeholder>
          )}
      </StyledSelectSearch>

      {articles && articles.articles && (
        <StyledSelectList>
          {articles.articles.map((article) => (
            <SelectOption
              value={[article.category.id, article.id]}
              key={article.id}
            >
              <ElkHighlighter text={article.title} />
            </SelectOption>
          ))}
        </StyledSelectList>
      )}
    </Select>
  );
}

NavSearchField.propTypes = {
  style: object,
  isWhiteHeader: bool,
};

NavSearchField.defaultProps = {
  style: {},
};

export default NavSearchField;
